import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';

import {
  createSavedTrip,
  deleteSavedTrip,
  listSavedTrips,
  patchSavedTrip,
  reorderSavedTrip,
} from '@/lib/api/endpoints';
import { isApiError } from '@/lib/api/error';
import { useUserAuth } from '@/lib/auth/UserAuthContext';
import type {
  SavedTripCreate,
  SavedTripLegRead,
  SavedTripLegWrite,
  SavedTripPatch,
  SavedTripRead,
} from '@/types';

const LOCAL_LEG_KEY = 'tt:savedStop:v1';
const PENDING_TRIP_KEY = 'tt:pendingTrip:v1';

// Cache key is scoped to the authenticated user so a sign-out → sign-in
// as a different user in the same browser doesn't expose the previous
// user's trips during the staleTime window. ``null`` is the anon slot
// (which we never populate, but keying it explicitly avoids accidental
// collisions with the keyless default).
function savedTripsKey(userId: number | null): readonly unknown[] {
  return ['saved-trips', userId] as const;
}

function useSavedTripsKey(): readonly unknown[] {
  const { user } = useUserAuth();
  return savedTripsKey(user ? user.id : null);
}

// ---------------------------------------------------------------------------
// LocalStorage adapter for the anon "try it" leg. The SaveSheet promotes
// this into a server-side trip on sign-in via
// `useMigrateLocalToServerOnSignin`.
// ---------------------------------------------------------------------------

interface LocalLeg extends SavedTripLegWrite {
  created_at: string;
  updated_at: string;
}

function isLocalLeg(value: unknown): value is LocalLeg {
  if (value === null || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  if (typeof v.from_stop_id !== 'string' || v.from_stop_id.length === 0) return false;
  // to_stop_id is optional (boarding-only leg) but, when present, must
  // be a non-empty string.
  if (v.to_stop_id != null && (typeof v.to_stop_id !== 'string' || v.to_stop_id.length === 0)) {
    return false;
  }
  if (typeof v.route_id !== 'string' || v.route_id.length === 0) return false;
  if (v.direction_id !== 0 && v.direction_id !== 1) return false;
  if (v.mode !== 'subway' && v.mode !== 'commuter_rail') return false;
  return true;
}

function loadLocalLeg(): LocalLeg | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(LOCAL_LEG_KEY);
    if (!raw) return null;
    // localStorage is user-controlled (or controllable via a malicious
    // browser extension); validate the shape before trusting it.
    const parsed: unknown = JSON.parse(raw);
    if (!isLocalLeg(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeLocalLeg(value: LocalLeg | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (value === null) {
      window.localStorage.removeItem(LOCAL_LEG_KEY);
    } else {
      window.localStorage.setItem(LOCAL_LEG_KEY, JSON.stringify(value));
    }
  } catch {
    // localStorage can be unavailable (private mode, quota); the
    // glance card stays usable, the choice just doesn't persist.
  }
}

function legFromLocal(local: LocalLeg): SavedTripLegRead {
  return {
    position: 0,
    from_stop_id: local.from_stop_id,
    to_stop_id: local.to_stop_id ?? null,
    route_id: local.route_id,
    direction_id: local.direction_id,
    mode: local.mode,
  };
}

function firstLegOf(trip: SavedTripRead | undefined | null): SavedTripLegRead | null {
  if (!trip) return null;
  const [first] = trip.legs;
  return first ?? null;
}

// ---------------------------------------------------------------------------
// useSavedTrips — the full list. Anon users see an empty list (their
// single localStorage leg is exposed via the legacy `useSavedStop`).
// ---------------------------------------------------------------------------

export function useSavedTrips(): {
  data: SavedTripRead[];
  isLoading: boolean;
} {
  const { status } = useUserAuth();
  const isAuthed = status === 'authenticated';
  const queryKey = useSavedTripsKey();
  const query = useQuery({
    queryKey,
    enabled: isAuthed,
    queryFn: async (): Promise<SavedTripRead[]> => listSavedTrips(),
    staleTime: 60_000,
  });
  if (!isAuthed) {
    return { data: [], isLoading: false };
  }
  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
  };
}

// ---------------------------------------------------------------------------
// Single-leg adapter that returns the first leg of the user's first
// saved trip (or the localStorage leg for anon users). Used by surfaces
// that only care about a single saved stop.
// ---------------------------------------------------------------------------

export function useSavedStop(): {
  data: SavedTripLegRead | null;
  isLoading: boolean;
  isLocal: boolean;
} {
  const { status } = useUserAuth();
  const isAuthed = status === 'authenticated';
  const [localLeg, setLocalLeg] = React.useState<LocalLeg | null>(() => loadLocalLeg());

  React.useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key !== LOCAL_LEG_KEY) return;
      setLocalLeg(loadLocalLeg());
    };
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener('storage', handler);
    };
  }, []);

  const { data: trips, isLoading } = useSavedTrips();

  if (isAuthed) {
    return {
      data: firstLegOf(trips[0]),
      isLoading,
      isLocal: false,
    };
  }
  return {
    data: localLeg ? legFromLocal(localLeg) : null,
    isLoading: false,
    isLocal: true,
  };
}

// ---------------------------------------------------------------------------
// Mutations.
// ---------------------------------------------------------------------------

export function useCreateTrip(): {
  mutate: (args: SavedTripCreate) => Promise<SavedTripRead>;
  isPending: boolean;
} {
  const queryKey = useSavedTripsKey();
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (args: SavedTripCreate): Promise<SavedTripRead> => {
      const created = await createSavedTrip(args);
      await qc.invalidateQueries({ queryKey });
      return created;
    },
  });
  return { mutate: mutation.mutateAsync, isPending: mutation.isPending };
}

export function usePatchTrip(): {
  mutate: (args: { id: number; body: SavedTripPatch }) => Promise<SavedTripRead>;
  isPending: boolean;
} {
  const queryKey = useSavedTripsKey();
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: number;
      body: SavedTripPatch;
    }): Promise<SavedTripRead> => {
      const patched = await patchSavedTrip(id, body);
      await qc.invalidateQueries({ queryKey });
      return patched;
    },
  });
  return { mutate: mutation.mutateAsync, isPending: mutation.isPending };
}

export function useDeleteTrip(): {
  mutate: (id: number) => Promise<void>;
  isPending: boolean;
} {
  const queryKey = useSavedTripsKey();
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await deleteSavedTrip(id);
    },
    // Optimistically drop the trip from the cached list so it disappears
    // immediately; snapshot the previous list so a failed delete rolls back.
    onMutate: async (id: number): Promise<{ previous: SavedTripRead[] | undefined }> => {
      await qc.cancelQueries({ queryKey });
      const previous = qc.getQueryData<SavedTripRead[]>(queryKey);
      qc.setQueryData<SavedTripRead[]>(queryKey, (prev) => (prev ?? []).filter((t) => t.id !== id));
      return { previous };
    },
    onError: (_err, _id, context): void => {
      if (context) {
        qc.setQueryData<SavedTripRead[]>(queryKey, context.previous);
      }
    },
    onSettled: async (): Promise<void> => {
      await qc.invalidateQueries({ queryKey });
    },
  });
  return { mutate: mutation.mutateAsync, isPending: mutation.isPending };
}

export function useReorderTrip(): {
  mutate: (args: { id: number; sort_order: number }) => Promise<SavedTripRead>;
  isPending: boolean;
} {
  const queryKey = useSavedTripsKey();
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: async ({
      id,
      sort_order,
    }: {
      id: number;
      sort_order: number;
    }): Promise<SavedTripRead> => {
      const updated = await reorderSavedTrip(id, { sort_order });
      await qc.invalidateQueries({ queryKey });
      return updated;
    },
  });
  return { mutate: mutation.mutateAsync, isPending: mutation.isPending };
}

// ---------------------------------------------------------------------------
// Pending-trip stash for the anon SaveSheet flow.
//
// An anonymous user fills out the SaveSheet, then submits. The submit
// triggers the magic-link send and parks the trip payload here; once
// the user lands signed-in via /auth/verify, `usePromotePendingTripOnSignin`
// drains it into the first server-side trip.
// ---------------------------------------------------------------------------

export interface PendingTrip {
  role: 'home' | 'work' | 'other';
  nickname: string | null;
  legs: SavedTripLegWrite[];
}

function isPendingTrip(value: unknown): value is PendingTrip {
  if (value === null || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  if (v.role !== 'home' && v.role !== 'work' && v.role !== 'other') return false;
  if (v.nickname !== null && typeof v.nickname !== 'string') return false;
  if (!Array.isArray(v.legs) || v.legs.length === 0) return false;
  return v.legs.every(isLocalLeg);
}

export function loadPendingTrip(): PendingTrip | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(PENDING_TRIP_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isPendingTrip(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writePendingTrip(value: PendingTrip | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (value === null) {
      window.sessionStorage.removeItem(PENDING_TRIP_KEY);
    } else {
      window.sessionStorage.setItem(PENDING_TRIP_KEY, JSON.stringify(value));
    }
  } catch {
    // sessionStorage may be unavailable (private mode); the anon save
    // gracefully degrades to a sign-in without a follow-up trip.
  }
}

/**
 * After sign-in, drain `sessionStorage.tt:pendingTrip:v1` into a real
 * saved trip and clear it. Runs once per session transition.
 */
export function usePromotePendingTripOnSignin(): void {
  const { status, user } = useUserAuth();
  const qc = useQueryClient();
  const promoted = React.useRef<number | null>(null);
  const inFlight = React.useRef(false);

  React.useEffect(() => {
    if (status !== 'authenticated' || !user) return;
    if (promoted.current === user.id) return;
    if (inFlight.current) return;
    const pending = loadPendingTrip();
    if (!pending) {
      promoted.current = user.id;
      return;
    }
    inFlight.current = true;
    void (async () => {
      try {
        const created = await createSavedTrip({
          role: pending.role,
          nickname: pending.nickname,
          legs: pending.legs,
        });
        qc.setQueryData<SavedTripRead[]>(savedTripsKey(user.id), (prev) =>
          prev ? [...prev, created] : [created],
        );
        writePendingTrip(null);
        promoted.current = user.id;
      } catch {
        // Best-effort; leaving the pending trip in sessionStorage means
        // the next mount retries.
      } finally {
        inFlight.current = false;
      }
    })();
  }, [qc, status, user]);
}

// ---------------------------------------------------------------------------
// Single-leg writers used by the design-canvas SaveStopConfirm variant.
// Authed writes hit the trip endpoint (creating a new trip or patching
// the first one); anon writes go to localStorage.
// ---------------------------------------------------------------------------

export function useSaveStop(): {
  mutate: (args: SavedTripLegWrite) => Promise<SavedTripLegRead>;
  isPending: boolean;
} {
  const { status } = useUserAuth();
  const isAuthed = status === 'authenticated';
  const queryKey = useSavedTripsKey();
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (args: SavedTripLegWrite): Promise<SavedTripLegRead> => {
      if (isAuthed) {
        const trips = await listSavedTrips();
        const existing = trips[0];
        if (existing) {
          const patched = await patchSavedTrip(existing.id, { legs: [args] });
          qc.setQueryData<SavedTripRead[]>(queryKey, (prev) =>
            (prev ?? []).map((t) => (t.id === patched.id ? patched : t)),
          );
          return (
            firstLegOf(patched) ?? { position: 0, ...args, to_stop_id: args.to_stop_id ?? null }
          );
        }
        const created = await createSavedTrip({ legs: [args] });
        qc.setQueryData<SavedTripRead[]>(queryKey, (prev) => [...(prev ?? []), created]);
        return firstLegOf(created) ?? { position: 0, ...args, to_stop_id: args.to_stop_id ?? null };
      }
      const now = new Date().toISOString();
      const saved: LocalLeg = { ...args, created_at: now, updated_at: now };
      writeLocalLeg(saved);
      window.dispatchEvent(new StorageEvent('storage', { key: LOCAL_LEG_KEY }));
      return {
        position: 0,
        from_stop_id: args.from_stop_id,
        to_stop_id: args.to_stop_id ?? null,
        route_id: args.route_id,
        direction_id: args.direction_id,
        mode: args.mode,
      };
    },
  });

  return { mutate: mutation.mutateAsync, isPending: mutation.isPending };
}

export function useClearStop(): {
  mutate: () => Promise<void>;
  isPending: boolean;
} {
  const { status } = useUserAuth();
  const isAuthed = status === 'authenticated';
  const queryKey = useSavedTripsKey();
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (): Promise<void> => {
      if (isAuthed) {
        const trips = await listSavedTrips();
        const existing = trips[0];
        if (existing) {
          await deleteSavedTrip(existing.id);
          qc.setQueryData<SavedTripRead[]>(queryKey, (prev) =>
            (prev ?? []).filter((t) => t.id !== existing.id),
          );
        }
        return;
      }
      writeLocalLeg(null);
      window.dispatchEvent(new StorageEvent('storage', { key: LOCAL_LEG_KEY }));
    },
  });

  return { mutate: mutation.mutateAsync, isPending: mutation.isPending };
}

/**
 * On first sign-in, promote the localStorage leg to a saved trip, if
 * any. Runs once per session transition: anon → authed.
 *
 * If the server already has any saved trips, the localStorage leg is
 * dropped (the server's choices win — localStorage was a "try it"
 * taste, not the source of truth for returning users).
 */
export function useMigrateLocalToServerOnSignin(): void {
  const { status, user } = useUserAuth();
  const qc = useQueryClient();
  // ``migrated.current`` only latches after a *successful* round-trip,
  // so a transient failure (5xx, network blip, missing CSRF on hard
  // reload) is retried on the next render — not silently lost for the
  // rest of the session.
  const migrated = React.useRef<number | null>(null);
  const inFlight = React.useRef(false);

  React.useEffect(() => {
    if (status !== 'authenticated' || !user) return;
    if (migrated.current === user.id) return;
    if (inFlight.current) return;
    const local = loadLocalLeg();
    if (!local) {
      migrated.current = user.id;
      return;
    }
    inFlight.current = true;
    void (async () => {
      try {
        let existing: SavedTripRead[] = [];
        try {
          existing = await listSavedTrips();
        } catch (err) {
          if (!isApiError(err) || err.status !== 401) throw err;
        }
        if (existing.length > 0) {
          writeLocalLeg(null);
          migrated.current = user.id;
          return;
        }
        const created = await createSavedTrip({
          legs: [
            {
              from_stop_id: local.from_stop_id,
              to_stop_id: local.to_stop_id ?? null,
              route_id: local.route_id,
              direction_id: local.direction_id,
              mode: local.mode,
            },
          ],
        });
        qc.setQueryData<SavedTripRead[]>(savedTripsKey(user.id), [created]);
        writeLocalLeg(null);
        migrated.current = user.id;
      } catch {
        // Best-effort; leaving the localStorage row + not setting
        // ``migrated.current`` means the next mount retries.
      } finally {
        inFlight.current = false;
      }
    })();
  }, [qc, status, user]);
}
